// src/Layouts/AdminLayout.tsx
// Updated to include new business intelligence dashboards

import React from 'react'
import {
  Home,
  BarChart3,
  Users,
  Activity,
  FileText,
  Gauge,
  Shield,
  MessageCircle,
  LayoutDashboard,
  Brain,
  TrendingUp,
  Database,
} from 'lucide-react'
import { useState, useEffect } from 'react'
import { Outlet, useNavigate, useLocation } from 'react-router'

import { NavUser } from '@/components/NavigationUser'
import { Avatar, AvatarImage } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import {
  SidebarInset,
  SidebarProvider,
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarFooter,
} from '@/components/ui/sidebar'

const ADMIN_NAV_DATA = [
  // Core Analytics Section
  {
    title: 'Query Analytics',
    url: '/admin/analytics',
    icon: BarChart3,
    description: 'Monitor LLM usage and patterns',
    isActive: true,
    category: 'analytics',
  },
  {
    title: 'Query Intelligence',
    url: '/admin/query-intelligence',
    icon: MessageCircle,
    description: 'Core business metrics and insights',
    isActive: true,
    category: 'analytics',
  },
  {
    title: 'User Analytics',
    url: '/admin/user-analytics',
    icon: Users,
    description: 'User engagement and behavior insights',
    isActive: true,
    category: 'analytics',
  },
  {
    title: 'Content & Knowledge',
    url: '/admin/content-knowledge',
    icon: LayoutDashboard,
    description: 'Product optimization insights',
    isActive: true,
    category: 'analytics',
  },
  {
    title: 'Predictive Analytics',
    url: '/admin/predictive-analytics',
    icon: Brain,
    description: 'Strategic planning and forecasting',
    isActive: true,
    category: 'analytics',
  },

  // System Management Section
  {
    title: 'User Management',
    url: '/admin/users',
    icon: Users,
    description: 'Manage users and permissions',
    isActive: true,
    category: 'management',
  },
  {
    title: 'System Health',
    url: '/admin/system',
    icon: Activity,
    description: 'Monitor system performance',
    isActive: true,
    category: 'system',
  },
  {
    title: 'Performance',
    url: '/admin/performance',
    icon: Gauge,
    description: 'Response times and metrics',
    isActive: true,
    category: 'system',
  },
  {
    title: 'Audit Logs',
    url: '/admin/logs',
    icon: FileText,
    description: 'System activity logs',
    isActive: true,
    category: 'system',
  },
]

// Group navigation items by category
const groupedNavData = ADMIN_NAV_DATA.reduce(
  (acc, item) => {
    if (!acc[item.category]) {
      acc[item.category] = []
    }
    acc[item.category].push(item)
    return acc
  },
  {} as Record<string, typeof ADMIN_NAV_DATA>,
)

export default function AdminLayout() {
  const [activeItem, setActiveItem] = useState(ADMIN_NAV_DATA[0])
  const navigate = useNavigate()
  const location = useLocation()

  // Update active item based on current location
  useEffect(() => {
    const currentItem = ADMIN_NAV_DATA.find(
      (item) => item.url === location.pathname,
    )
    if (currentItem) {
      setActiveItem(currentItem)
    }
  }, [location.pathname])

  return (
    <SidebarProvider
      style={
        {
          '--sidebar-width': '48px',
          '--sidebar-width-mobile': '20rem',
        } as React.CSSProperties
      }
    >
      <Sidebar
        collapsible="icon"
        className="overflow-hidden [&>[data-sidebar=sidebar]]:flex-row bg-background"
      >
        <Sidebar
          collapsible="none"
          className="!w-[calc(var(--sidebar-width-icon)_+_1px)] border-r"
        >
          {/* Admin Logo/Icon Header */}
          <SidebarHeader>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton size="lg" asChild className="md:h-8 md:p-0">
                  <Avatar
                    className="w-16 h-16 -ml-1 transition-all duration-200 hover:scale-105 hover:shadow-lg cursor-pointer"
                    title="Admin Panel"
                  >
                    <div className="w-12 h-12 bg-red-600 rounded-lg flex items-center justify-center">
                      <Shield className="w-8 h-8 text-white" />
                    </div>
                  </Avatar>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarHeader>

          <SidebarContent>
            {/* Back to Main App Button */}
            <SidebarGroup>
              <SidebarGroupContent className="px-1.5 md:px-0">
                <SidebarMenu>
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      tooltip={{
                        children: 'Back to Main App',
                        hidden: false,
                      }}
                      onClick={() => navigate('/chat')}
                      className="px-2.5 md:px-2 mb-2 border-b border-border pb-2"
                    >
                      <Home />
                      <span>Back to Main</span>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>

            {/* All Admin Navigation Items */}
            <SidebarGroup>
              <SidebarGroupContent className="px-1.5 md:px-0">
                <SidebarMenu>
                  {ADMIN_NAV_DATA.map((item) => (
                    <SidebarMenuItem key={item.title}>
                      <SidebarMenuButton
                        tooltip={{
                          children: item.description,
                          hidden: false,
                        }}
                        onClick={async () => {
                          setActiveItem(item)
                          await navigate(item.url)
                        }}
                        isActive={activeItem.title === item.title}
                        className="px-2.5 md:px-2"
                      >
                        <item.icon />
                        <span>{item.title}</span>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          </SidebarContent>

          <SidebarFooter>
            <NavUser />
          </SidebarFooter>
        </Sidebar>
      </Sidebar>

      {/* Main Admin Content - Same as MainLayout */}
      <SidebarInset className="flex-1 overflow-hidden">
        <Outlet />
      </SidebarInset>
    </SidebarProvider>
  )
}
